import React, { useState } from 'react';
import { MessageCircle, CornerDownRight, Send, User, X } from 'lucide-react';

export const CommentSection = ({ comments, onAddComment }) => {
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    onAddComment(newComment, replyTo?.id);
    setNewComment('');
    setReplyTo(null);
  };

  const renderComment = (comment, isReply = false) => (
    <div key={comment.id} className={`${isReply ? 'ml-10 mt-6' : 'mb-10'} animate-in slide-in-from-left-4 duration-500`}>
      <div className="flex gap-4 items-start">
        <div className="relative">
          {comment.author_avatar ? (
            <img src={comment.author_avatar} alt={comment.author} className="w-10 h-10 rounded-2xl object-cover shadow-md border-2 border-white dark:border-stone-900" />
          ) : (
            <div className="w-10 h-10 rounded-2xl bg-stone-100 flex items-center justify-center text-stone-400"><User size={20}/></div>
          )}
          {isReply && <div className="absolute -left-6 top-5 w-5 h-5 border-l-2 border-b-2 border-stone-100 dark:border-stone-800 rounded-bl-xl" />}
        </div>
        
        <div className="flex-1">
          <div className="bg-white dark:bg-stone-900 p-5 rounded-[28px] border border-stone-100 dark:border-stone-800 shadow-sm hover:shadow-md transition-shadow group">
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-black text-stone-900 dark:text-white uppercase tracking-widest">@{comment.author}</p>
              <span className="text-[10px] text-stone-300 font-black uppercase tracking-[0.2em]">Reciente</span>
            </div>
            <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed font-medium">{comment.text}</p>
          </div>
          
          <div className="flex gap-6 mt-3 ml-4">
            <button 
              onClick={() => setReplyTo({ id: comment.id, author: comment.author })}
              className="text-[10px] font-black text-stone-400 hover:text-primary transition-colors uppercase tracking-[0.2em] flex items-center gap-2"
            >
              <CornerDownRight size={14}/> Responder
            </button>
          </div>
          
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-2 space-y-4">
              {comment.replies.map(reply => renderComment(reply, true))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-12 max-w-3xl">
      <div className="flex items-center justify-between mb-10">
        <h3 className="text-3xl font-black heading-serif dark:text-white flex items-center gap-4">
          <div className="p-3 bg-stone-100 dark:bg-stone-800 rounded-2xl text-stone-400"><MessageCircle size={24} /></div>
          Conversación
        </h3>
        <span className="px-4 py-2 bg-stone-100 dark:bg-stone-800 rounded-full text-[10px] font-black uppercase tracking-widest text-stone-400">
          {comments.length} Comentarios
        </span>
      </div>

      <div className="mb-12">
        <form onSubmit={handleSubmit} className="relative group">
          {replyTo && (
            <div className="mb-4 flex items-center justify-between bg-primary/5 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest text-primary animate-in slide-in-from-top-2">
              <span className="flex items-center gap-2"><CornerDownRight size={14}/> Respondiendo a @{replyTo.author}</span>
              <button type="button" onClick={() => setReplyTo(null)} className="hover:text-stone-900 dark:hover:text-white"><X size={14}/></button>
            </div>
          )}
          <div className="relative">
            <textarea 
              data-testid="comment-input"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={replyTo ? `Escribe tu respuesta...` : "Comparte tu opinión o pregunta al chef..."}
              className="w-full p-6 pr-20 rounded-[32px] bg-white dark:bg-stone-900 border border-stone-100 dark:border-stone-800 shadow-sm focus:ring-4 focus:ring-primary/5 focus:border-primary outline-none transition-all resize-none h-32 text-sm font-medium dark:text-white"
            />
            <button 
              data-testid="submit-comment-btn"
              type="submit"
              disabled={!newComment.trim()}
              className="absolute right-4 bottom-4 p-4 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 rounded-2xl hover:bg-primary dark:hover:bg-primary dark:hover:text-white transition-all shadow-xl active:scale-95 disabled:opacity-20"
            >
              <Send size={20} />
            </button>
          </div>
        </form>
      </div>

      <div className="space-y-6">
        {comments.length > 0 ? (
          comments.map(c => renderComment(c))
        ) : (
          <div className="text-center py-20 bg-stone-50 dark:bg-stone-900/50 rounded-[40px] border-2 border-dashed border-stone-100 dark:border-stone-800">
            <MessageCircle size={48} className="mx-auto mb-4 text-stone-200" />
            <p className="text-stone-400 font-black uppercase tracking-widest text-xs">Sé el primero en romper el hielo</p>
          </div>
        )}
      </div>
    </div>
  );
};
